import React, { useMemo } from "react";
import { Clock, AlertTriangle, CheckCircle, Info } from "lucide-react";
import { calcolaBolloAnnuale } from "../../utils/bolloCalc";
import { BOLLO_CODICE_TRIBUTO } from "../../constants/bollo";

function formatCurrency(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value);
}

function formatDate(date) {
  return new Date(date).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getStatoTrimestre(trimestre) {
  if (trimestre.importoDovuto === 0) return "nessuno";

  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  const scadenza = new Date(trimestre.dataScadenza);
  scadenza.setHours(0, 0, 0, 0);

  if (scadenza < oggi) return "scaduto";
  if (trimestre.puoAccorpare) return "accorpabile";
  return "da_pagare";
}

function getCountdown(dataScadenza) {
  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  const scadenza = new Date(dataScadenza);
  scadenza.setHours(0, 0, 0, 0);
  const diffMs = scadenza - oggi;
  const giorni = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (giorni < 0) return null;
  if (giorni === 0) return "Oggi";
  if (giorni === 1) return "Domani";
  return `${giorni} giorni`;
}

export default function BolloSection({ invoices, selectedYear, darkMode = false }) {
  const bolloData = useMemo(
    () => calcolaBolloAnnuale(invoices, selectedYear),
    [invoices, selectedYear]
  );

  const getStatoBadge = (stato) => {
    switch (stato) {
      case "scaduto":
        return {
          label: "Scaduto",
          className: darkMode
            ? "bg-red-900/50 text-red-300"
            : "bg-red-100 text-red-700",
          icon: <AlertTriangle className="w-3 h-3" />,
        };
      case "da_pagare":
        return {
          label: "Da pagare",
          className: darkMode
            ? "bg-amber-900/50 text-amber-300"
            : "bg-amber-100 text-amber-700",
          icon: <Clock className="w-3 h-3" />,
        };
      case "accorpabile":
        return {
          label: "Accorpabile",
          className: darkMode
            ? "bg-blue-900/50 text-blue-300"
            : "bg-blue-100 text-blue-700",
          icon: <Info className="w-3 h-3" />,
        };
      default:
        return {
          label: "Nessun bollo",
          className: darkMode
            ? "bg-neutral-800 text-neutral-500"
            : "bg-neutral-100 text-neutral-400",
          icon: <CheckCircle className="w-3 h-3" />,
        };
    }
  };

  return (
    <div className="space-y-4">
      {/* Riepilogo annuale */}
      <div className="grid grid-cols-2 gap-4">
        <div
          className={`p-4 rounded-xl ${
            darkMode ? "bg-neutral-800" : "bg-neutral-50"
          }`}
        >
          <div
            className={`text-xs ${
              darkMode ? "text-neutral-500" : "text-neutral-500"
            }`}
          >
            Fatture con bollo
          </div>
          <div
            className={`text-2xl font-bold mt-1 ${
              darkMode ? "text-white" : "text-neutral-900"
            }`}
          >
            {bolloData.totaleFatture}
          </div>
        </div>
        <div
          className={`p-4 rounded-xl ${
            darkMode ? "bg-neutral-800" : "bg-neutral-50"
          }`}
        >
          <div
            className={`text-xs ${
              darkMode ? "text-neutral-500" : "text-neutral-500"
            }`}
          >
            Totale bollo {selectedYear}
          </div>
          <div
            className={`text-2xl font-bold mt-1 ${
              bolloData.totaleAnnuo > 0
                ? darkMode
                  ? "text-amber-400"
                  : "text-amber-600"
                : darkMode
                ? "text-neutral-500"
                : "text-neutral-400"
            }`}
          >
            {formatCurrency(bolloData.totaleAnnuo)}
          </div>
        </div>
      </div>

      {/* Tabella trimestri */}
      <div
        className={`rounded-xl border overflow-hidden ${
          darkMode ? "border-neutral-800" : "border-neutral-200"
        }`}
      >
        <table className="w-full text-sm">
          <thead>
            <tr
              className={darkMode ? "bg-neutral-800" : "bg-neutral-100"}
            >
              <th
                className={`px-3 py-2.5 text-left font-medium ${
                  darkMode ? "text-neutral-300" : "text-neutral-700"
                }`}
              >
                Trimestre
              </th>
              <th
                className={`px-3 py-2.5 text-center font-medium ${
                  darkMode ? "text-neutral-300" : "text-neutral-700"
                }`}
              >
                Fatture
              </th>
              <th
                className={`px-3 py-2.5 text-right font-medium ${
                  darkMode ? "text-neutral-300" : "text-neutral-700"
                }`}
              >
                Importo
              </th>
              <th
                className={`px-3 py-2.5 text-center font-medium hidden sm:table-cell ${
                  darkMode ? "text-neutral-300" : "text-neutral-700"
                }`}
              >
                Scadenza
              </th>
              <th
                className={`px-3 py-2.5 text-right font-medium ${
                  darkMode ? "text-neutral-300" : "text-neutral-700"
                }`}
              >
                Stato
              </th>
            </tr>
          </thead>
          <tbody>
            {bolloData.trimestri.map((trim, idx) => {
              const stato = getStatoTrimestre(trim);
              const badge = getStatoBadge(stato);
              return (
                <tr
                  key={trim.trimestre}
                  className={
                    darkMode
                      ? idx % 2 === 0
                        ? "bg-neutral-900"
                        : "bg-neutral-800/50"
                      : idx % 2 === 0
                      ? "bg-white"
                      : "bg-neutral-50"
                  }
                >
                  <td
                    className={`px-3 py-2.5 ${
                      darkMode ? "text-neutral-300" : "text-neutral-700"
                    }`}
                  >
                    <span className="font-medium">{trim.trimestre}</span>
                    <span
                      className={`block text-xs ${
                        darkMode ? "text-neutral-500" : "text-neutral-500"
                      }`}
                    >
                      {trim.label}
                    </span>
                  </td>
                  <td
                    className={`px-3 py-2.5 text-center tabular-nums ${
                      darkMode ? "text-neutral-300" : "text-neutral-700"
                    }`}
                  >
                    {trim.numeroFatture}
                  </td>
                  <td
                    className={`px-3 py-2.5 text-right tabular-nums font-medium ${
                      trim.importoDovuto > 0
                        ? darkMode
                          ? "text-white"
                          : "text-neutral-900"
                        : darkMode
                        ? "text-neutral-500"
                        : "text-neutral-400"
                    }`}
                  >
                    {trim.importoDovuto > 0
                      ? formatCurrency(trim.importoDovuto)
                      : "—"}
                  </td>
                  <td
                    className={`px-3 py-2.5 text-center text-xs hidden sm:table-cell ${
                      darkMode ? "text-neutral-400" : "text-neutral-600"
                    }`}
                  >
                    {formatDate(trim.dataScadenza)}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                    >
                      {badge.icon}
                      {badge.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Prossima scadenza con countdown */}
      {bolloData.prossimaScadenza && (
        <div
          className={`flex items-center justify-between p-4 rounded-xl border ${
            darkMode
              ? "border-amber-700/50 bg-amber-900/20"
              : "border-amber-200 bg-amber-50"
          }`}
        >
          <div>
            <div
              className={`text-xs font-medium uppercase tracking-wide ${
                darkMode ? "text-amber-400" : "text-amber-700"
              }`}
            >
              Prossima scadenza bollo
            </div>
            <div
              className={`text-sm mt-1 ${
                darkMode ? "text-amber-300" : "text-amber-800"
              }`}
            >
              {bolloData.prossimaScadenza.trimestre} —{" "}
              {formatDate(bolloData.prossimaScadenza.dataScadenza)}
            </div>
          </div>
          <div className="text-right">
            <div
              className={`text-lg font-bold ${
                darkMode ? "text-amber-300" : "text-amber-700"
              }`}
            >
              {formatCurrency(bolloData.prossimaScadenza.importoDovuto)}
            </div>
            <div
              className={`text-xs ${
                darkMode ? "text-amber-400" : "text-amber-600"
              }`}
            >
              {getCountdown(bolloData.prossimaScadenza.dataScadenza) || "Scaduto"}
            </div>
          </div>
        </div>
      )}

      {/* Info codice tributo */}
      <div
        className={`flex items-start gap-2 p-3 rounded-lg ${
          darkMode ? "bg-neutral-800" : "bg-neutral-100"
        }`}
      >
        <Info
          className={`w-4 h-4 mt-0.5 shrink-0 ${
            darkMode ? "text-neutral-500" : "text-neutral-500"
          }`}
        />
        <p
          className={`text-xs ${
            darkMode ? "text-neutral-400" : "text-neutral-600"
          }`}
        >
          Codice tributo F24:{" "}
          <strong className={darkMode ? "text-white" : "text-neutral-900"}>
            {BOLLO_CODICE_TRIBUTO}
          </strong>{" "}
          — Versamento tramite modello F24 con i codici dell'Agenzia delle
          Entrate. Bollo: 2€ per fatture superiori a 77,47€.
        </p>
      </div>
    </div>
  );
}
